import Udesk from 'Udesk';
import UdeskLocales from 'UdeskLocales';
import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Button, Input, Switch, message } from 'udesk-ui';
import Page from 'Component/common/udesk-qa-page-layout';
import CommonTablePage from 'Component/common/udesk-qa-table-page';
import type { TableComponentProps } from 'Component/common/udesk-qa-table-page';
import { AiAgentTypeEnum, TypeEnum } from './const';
import { getAiAgents } from 'src/api/aiAgents';
import { putAiAgentsStatusById } from 'src/api/aiAgents/status/{id}';
import { deleteAiAgentsById } from 'src/api/aiAgents/{id}';
import './style.scss';

const Template = React.memo((props: any) => {
    const [getLoading, setGetLoading] = useState(false);
    const [dataSource, setDataSource] = useState<any[]>([]);
    const [paging, setPaging] = useState({
        pageNum: 1,
        pageSize: 20,
        total: 0,
    });
    const ref = useRef<any>({});

    const queryList = useCallback(
        ({
            pageSize,
            pageNum,
            keyword,
        }: {
            pageSize: number;
            pageNum: number;
            keyword?: string;
        }) => {
            setGetLoading(true);
            getAiAgents({
                params: {
                    pageNum,
                    pageSize,
                    keyword,
                },
            }).then((resp) => {
                setDataSource(resp.data || []);
                setGetLoading(false);
                setPaging(resp.paging as any);
            });
        },
        []
    );

    useEffect(() => {
        queryList({ pageSize: 20, pageNum: 1 });
    }, [queryList]);

    const paginationProps = useMemo(() => {
        const { pageSize, total } = paging;
        return {
            current: paging.pageNum,
            pageSize,
            total,
            onChange: (page, pageSize) => {
                const newPages = {
                    ...paging,
                    pageNum: page,
                    pageSize,
                };
                queryList({ ...newPages });
                setPaging(newPages);
            },
        };
    }, [paging, queryList]);

    const [putLoading, setPutLoading] = useState(false);
    const [delLoading, setDelLoading] = useState(false);

    const columns = useMemo(
        () => [
            {
                title: /* 名称 */UdeskLocales['current'].pages.semanticIntelligence.aiAgent.index.name,
                dataIndex: 'agentName',
                key: 'agentName',
            },
            {
                title: /* 描述 */UdeskLocales['current'].pages.semanticIntelligence.aiAgent.index.description,
                dataIndex: 'agentDesc',
                key: 'agentDesc',
            },
            {
                key: 'source',
                dataIndex: 'source',
                title: /* 来源 */UdeskLocales['current'].pages.semanticIntelligence.aiAgent.index.source,
                render: (v) => {
                    if (v === AiAgentTypeEnum.Udesk) {
                        return 'Udesk';
                    } else if (v === AiAgentTypeEnum.Coze) {
                        return 'Coze';
                    } else {
                        return '-';
                    }
                },
            },
            {
                title: /* 类型 */ UdeskLocales['current'].pages.semanticIntelligence
                    .intelligentModel.index.type,
                dataIndex: 'type',
                key: 'type',
                render: (_, record) => {
                    if (record.type === TypeEnum.Conform) {
                        return /* 是否符合 */ UdeskLocales['current'].pages.semanticIntelligence
                            .intelligentModel.index.whetherItMeetsTheRequirements;
                    } else if (record.type === TypeEnum.GradedEvaluation) {
                        return /* 分档评价 */ UdeskLocales['current'].pages.semanticIntelligence
                            .intelligentModel.index.gradedEvaluation;
                    } else {
                        return '-';
                    }
                },
            },
            {
                key: 'status',
                dataIndex: 'status',
                title: /* 有效性 */ UdeskLocales['current'].pages.semanticIntelligence
                    .intelligentModel.index.validity,
                render: (v, record) => {
                    return (
                        <Switch
                            checked={v === 1}
                            onChange={() => {
                                setPutLoading(true);
                                putAiAgentsStatusById(
                                    {
                                        ...record,
                                        id: record.id,
                                        status: v === 1 ? 0 : 1,
                                    },
                                    {
                                        segments: {
                                            id: record.id,
                                        },
                                    }
                                )
                                    .then((res) => {
                                        message.success(/* 状态更新成功 */UdeskLocales['current'].pages.semanticIntelligence.aiAgent.index.statusUpdateSuccessful);
                                        queryList({
                                            pageNum: paging.pageNum,
                                            pageSize: paging.pageSize,
                                        });
                                    })
                                    .finally(() => {
                                        setPutLoading(false);
                                    });
                            }}
                        />
                    );
                },
            },
            {
                dataIndex: 'actions',
                title: UdeskLocales['current'].labels.action,
                renderType: 'actions',
                onEdit: (item) => {
                    let routeOptions = {
                        history: props.history,
                        routeName: 'aiAgentEdit',
                        pathParams: { id: item.id },
                        state: {},
                    };
                    Udesk.ui.routing.transitionTo(routeOptions);
                },
                onDelete: (item) => {
                    setDelLoading(true);
                    deleteAiAgentsById({
                        segments: {
                            id: item.id,
                        },
                    })
                        .then((res) => {
                            message.success(
                                UdeskLocales['current'].business.notifyMessage.deleteSuccess
                            );
                            queryList({
                                pageNum: 1,
                                pageSize: paging.pageSize,
                            });
                        })
                        .finally(() => {
                            setDelLoading(false);
                        });
                },
            },
        ],
        [paging.pageNum, paging.pageSize, props.history, queryList]
    );

    const tableProps = useMemo<TableComponentProps>(() => {
        return {
            columns,
            dataSource,
            cacheKey: false,
        };
    }, [columns, dataSource]);

    const onSearch = useCallback(
        (value?: string) => {
            queryList({ pageSize: paging.pageSize, pageNum: 1, keyword: value });
        },
        [paging.pageSize, queryList]
    );

    const createBtn = (
        <Button
            onClick={() => {
                let routeOptions = {
                    history: props.history,
                    routeName: 'aiAgentNew',
                    state: {},
                };
                Udesk.ui.routing.transitionTo(routeOptions);
            }}
        >
            {UdeskLocales['current'].labels.create}
        </Button>
    );

    const loading = getLoading || putLoading || delLoading;

    return (
        <Page pageBodyClassName="knowledge-base-page-index" title={/* 智能体 */UdeskLocales['current'].pages.semanticIntelligence.aiAgent.index.agent} padding={true}>
            <CommonTablePage
                ref={ref}
                loading={loading}
                mainButton={createBtn}
                tableHeaderLeftRender={
                    <Input.Search
                        style={{ width: 300 }}
                        placeholder={/* 请输入关键字 */UdeskLocales['current'].pages.semanticIntelligence.aiAgent.index.pleaseEnterAKeyword}
                        onSearch={onSearch}
                        allowClear
                        maxLength={200}
                    />
                }
            >
                <CommonTablePage.Table {...tableProps}></CommonTablePage.Table>
                <CommonTablePage.Pagination {...paginationProps}></CommonTablePage.Pagination>
            </CommonTablePage>
        </Page>
    );
});

class Component extends React.Component {
    render() {
        return <Template {...this.props} />;
    }
}

export default Udesk.react.udeskify(Component);
